import React, { useEffect, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import AccountSettingsNav from '@/components/AccountSettingsNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import CreateGroupDialog from '@/components/account/groups/CreateGroupDialog'
import EditGroupDialog from '@/components/account/groups/EditGroupDialog'
import AddHotelDialog from '@/components/account/groups/AddHotelDialog'
import DecoupleMemberDialog, { type DecoupleResult } from '@/components/account/groups/DecoupleMemberDialog'
import DissolveGroupDialog from '@/components/account/groups/DissolveGroupDialog'
import GroupChangeReport from '@/components/account/groups/GroupChangeReport'
import CombinedInventoryNotices from '@/components/account/groups/CombinedInventoryNotices'
import MappingSavedNotice from '@/components/account/groups/MappingSavedNotice'
import type { MappingResult } from '@/lib/combined-inventory'
import { countListings, groupConsequences, type GroupCandidate, type GroupMember, type PropertyGroupRow } from '@/lib/groups'
import {
  Layers,
  Crown,
  Plus,
  Trash2,
  Unlink,
  Check,
  Info,
  ArrowRight,
  PauseCircle,
  Building2,
  Link2,
  Percent,
} from 'lucide-react'
import type { AccountSettingsSharedProps } from '@/types/shared'

interface GroupsProps extends AccountSettingsSharedProps {
  groups: PropertyGroupRow[]
  /** The account's UNGROUPED hotels — what a new group can be built from. */
  candidates: GroupCandidate[]
  canManageGroups: boolean
  /** What a listing joining just did (App::RoomMappingOutcome), or null. */
  mappingResult?: MappingResult | null
}

// BUSINESS SETTINGS → COMBINED INVENTORY (the `groups` route, PropertyGroup in code).
//
// One building listed several times in the PMS, priced from one lead listing
// (docs/plans/2026-10-16-combined-inventory.md; rule 1 locks the screen words). Every card states
// where prices are set, that every listing's bookings count toward the building, and what stays per
// listing (publishing).
//
// THE SHAPE OF A GROUP IS THE POINT, so the member list is rendered as a lead-anchored ladder rather
// than a table of equal rows: the lead sits at the top with the crown, and its members hang off a
// rail beneath it. Which hotel governs the pricing is the single fact an operator comes to this page
// for, and a hierarchy reads it at a glance where a "Lead" column has to be looked for.
//
// MOST BUSINESSES WILL NEVER USE THIS. The empty state therefore explains what combined inventory is
// and who it is for, and says outright that not needing it is normal: an empty page that reads like a
// missing setup step is how a feature nobody asked for becomes a support queue.
export default function Groups({
  currentUser,
  currentAccount,
  accountAbilities,
  activeSection,
  groups,
  candidates,
  canManageGroups,
  mappingResult,
}: GroupsProps) {
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<PropertyGroupRow | null>(null)
  const [addingTo, setAddingTo] = useState<PropertyGroupRow | null>(null)
  const [dissolving, setDissolving] = useState<PropertyGroupRow | null>(null)
  const [decoupling, setDecoupling] = useState<{ group: PropertyGroupRow; member: GroupMember } | null>(null)
  // The report of what a decouple/dissolve actually did. Persistent, not a toast: it is a worklist of
  // rooms that now need a base rate, and it must survive the page reload that follows the write.
  const [report, setReport] = useState<DecoupleResult | null>(null)
  // What a room mapping save did. Arrives with the page that follows the save; dismissable.
  const [saved, setSaved] = useState<MappingResult | null>(mappingResult ?? null)
  useEffect(() => {
    setSaved(mappingResult ?? null)
  }, [mappingResult])

  function onSplitDone(result: DecoupleResult) {
    setReport(result)
    setDecoupling(null)
    setDissolving(null)
    router.reload({ only: ['groups', 'candidates'] })
  }

  return (
    <PortfolioLayout title="Combined inventory" currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Business settings</h1>
            <HelpTooltip content="Combined inventory is one building listed several times in your PMS: prices are set at the lead listing, and bookings on every listing count toward the building." />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">Buildings you sell through several PMS listings.</p>
        </section>

        <AccountSettingsNav accountAbilities={accountAbilities} activeSection={activeSection} settingsPath={currentAccount.settingsPath} />

        {report && <GroupChangeReport result={report} onDismiss={() => setReport(null)} />}
        {saved && <MappingSavedNotice result={saved} onDismiss={() => setSaved(null)} />}

        {groups.length === 0 ? (
          <EmptyState canCreate={canManageGroups && candidates.length > 0} onCreate={() => setCreating(true)} />
        ) : (
          <>
            {groups.map((group, index) => (
              <GroupCard
                key={group.id}
                group={group}
                index={index}
                canManageGroups={canManageGroups}
                onEdit={() => setEditing(group)}
                onAddHotel={() => setAddingTo(group)}
                onDissolve={() => setDissolving(group)}
                onDecouple={(member) => setDecoupling({ group, member })}
              />
            ))}

            {canManageGroups && (
              <section className="animate-slide-in" style={{ animationDelay: `${100 + groups.length * 50}ms` }}>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-border bg-white px-5 py-4 shadow-sm">
                  <p className="text-xs text-muted-foreground">
                    {candidates.length > 0
                      ? `${countListings(candidates.length)} in this business ${candidates.length === 1 ? 'is' : 'are'} not in a combined inventory.`
                      : 'Every listing in this business is already in a combined inventory.'}
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 rounded-lg text-xs"
                    onClick={() => setCreating(true)}
                    disabled={candidates.length === 0}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    New combined inventory
                  </Button>
                </div>
              </section>
            )}
          </>
        )}
      </div>

      <CreateGroupDialog settingsPath={currentAccount.settingsPath} open={creating} candidates={candidates} onClose={() => setCreating(false)} />
      <EditGroupDialog settingsPath={currentAccount.settingsPath} group={editing} onClose={() => setEditing(null)} />
      <AddHotelDialog settingsPath={currentAccount.settingsPath} group={addingTo} onClose={() => setAddingTo(null)} />
      <DecoupleMemberDialog settingsPath={currentAccount.settingsPath}
        group={decoupling?.group ?? null}
        member={decoupling?.member ?? null}
        onClose={() => setDecoupling(null)}
        onDone={onSplitDone}
      />
      <DissolveGroupDialog settingsPath={currentAccount.settingsPath} group={dissolving} onClose={() => setDissolving(null)} onDone={onSplitDone} />
    </PortfolioLayout>
  )
}

// ── One combined inventory ───────────────────────────────────────────────────
interface GroupCardProps {
  group: PropertyGroupRow
  index: number
  canManageGroups: boolean
  onEdit: () => void
  onAddHotel: () => void
  onDissolve: () => void
  onDecouple: (member: GroupMember) => void
}

function GroupCard({ group, index, canManageGroups, onEdit, onAddHotel, onDissolve, onDecouple }: GroupCardProps) {
  const lead = group.members.find((member) => member.isLead)
  const followers = group.members.filter((member) => !member.isLead)
  const attachable = group.candidates.filter((candidate) => candidate.eligible)

  return (
    <section className="animate-slide-in" style={{ animationDelay: `${100 + index * 50}ms` }}>
      <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
        {/* Header: the combined inventory, its size, and what may be done to it. It is called by its
            lead listing's name (`group.name` is the lead's, server-side), and has no name of its own. */}
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/40 px-5 py-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground">{group.name}</h2>
              <Badge variant="outline" className="border-primary/20 bg-primary/5 text-[11px] text-primary">
                {countListings(group.hotelCount)}
              </Badge>
              {group.currency && (
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{group.currency}</span>
              )}
            </div>
            <p className="mt-1 text-xs tabular-nums text-muted-foreground">
              {group.roomTypeCount} room {group.roomTypeCount === 1 ? 'type' : 'types'} · {group.unitCount}{' '}
              {group.unitCount === 1 ? 'room' : 'rooms'} in the building
            </p>
          </div>

          {canManageGroups && (
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 rounded-lg text-xs" onClick={onEdit}>
                <Crown className="mr-1.5 h-3.5 w-3.5" />
                Change lead listing
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 rounded-lg text-xs"
                onClick={onAddHotel}
                disabled={group.candidates.length === 0}
                title={group.candidates.length === 0 ? 'Every listing in this business is already in a combined inventory.' : undefined}
              >
                <Plus className="mr-1.5 h-3.5 w-3.5" />
                Add listing
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 rounded-lg text-xs text-rose-500 hover:text-rose-600"
                onClick={onDissolve}
              >
                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                Split up
              </Button>
            </div>
          )}
        </div>

        {/* Where prices are set, whose bookings count, and what stays per listing. */}
        <div className="space-y-1.5 border-b border-border/40 bg-primary/[0.03] px-5 py-3.5">
          {groupConsequences(group).map((sentence) => (
            <p key={sentence} className="flex items-start gap-2 text-xs leading-relaxed text-foreground">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              <span>{sentence}</span>
            </p>
          ))}
        </div>

        {/* What needs the operator now: an unsound Same-room pair, a following listing not publishing. */}
        {group.notices && group.notices.length > 0 && (
          <div className="border-b border-border/40 px-5 py-3.5">
            <CombinedInventoryNotices notices={group.notices} />
          </div>
        )}

        {/* The ladder: the lead listing, then its following listings hanging off a rail. */}
        <div className="px-5 py-4">
          {lead && (
            <MemberRow member={lead} group={group} canManageGroups={canManageGroups} onDecouple={onDecouple} />
          )}

          {followers.length > 0 && (
            <div className="relative mt-1 pl-6">
              <span
                className="absolute left-[11px] top-0 bottom-6 w-px bg-border"
                aria-hidden="true"
              />
              {followers.map((member) => (
                <div key={member.id} className="relative">
                  <span className="absolute -left-[13px] top-6 h-px w-3 bg-border" aria-hidden="true" />
                  <MemberRow member={member} group={group} canManageGroups={canManageGroups} onDecouple={onDecouple} />
                </div>
              ))}
            </div>
          )}

          {group.hotelCount === 1 && (
            <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {group.leadPropertyName} is the only listing in this combined inventory, so it prices exactly as it would
                on its own.
                {attachable.length > 0 && ' Add a following listing to price them together.'}
              </span>
            </p>
          )}
        </div>
      </div>
    </section>
  )
}

// ── One listing inside a combined inventory ──────────────────────────────────
interface MemberRowProps {
  member: GroupMember
  group: PropertyGroupRow
  canManageGroups: boolean
  onDecouple: (member: GroupMember) => void
}

function MemberRow({ member, group, canManageGroups, onDecouple }: MemberRowProps) {
  const unpriced = member.decouplePreview?.severed.filter((room) => room.willBeUnpriced).length ?? 0

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-lg px-3 py-3 ${
        member.isLead ? 'border border-primary/20 bg-primary/5' : 'border border-transparent hover:bg-muted/40'
      }`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          {member.isLead ? (
            <Crown className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          ) : (
            <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <Link
            href={member.basePath}
            className="truncate text-sm font-medium text-foreground transition-colors hover:text-primary"
          >
            {member.name}
          </Link>
          {member.isLead && (
            <Badge className="bg-primary text-[11px] text-primary-foreground hover:bg-primary">Lead listing</Badge>
          )}
          {member.dryRun && (
            <Badge
              variant="outline"
              className="inline-flex items-center gap-1 border-amber-200 bg-amber-50 text-[11px] text-amber-700"
            >
              <PauseCircle className="h-3 w-3" />
              Paused
            </Badge>
          )}
        </div>
        <p className="mt-0.5 pl-[22px] text-xs tabular-nums text-muted-foreground">
          {member.roomTypeCount} room {member.roomTypeCount === 1 ? 'type' : 'types'} · {member.unitCount} rooms
          {member.isLead && ' · prices are set here'}
        </p>
        {/* A following listing's room mapping at a glance: how many of its rooms are Same rooms of a
            lead room and how many are Added rooms. Counted and worded by the server, linking to its room
            mapping page. */}
        {member.mapping && member.roomTypeCount > 0 && (
          <p className="mt-0.5 flex items-center gap-1.5 pl-[22px] text-xs tabular-nums text-muted-foreground">
            <Link2 className="h-3 w-3 shrink-0 text-primary/70" aria-hidden="true" />
            {member.mappingHref ? (
              <Link href={member.mappingHref} className="rounded hover:text-foreground hover:underline">
                {member.mapping.summary}
              </Link>
            ) : (
              member.mapping.summary
            )}
          </p>
        )}
        {/* Its Same rooms' offsets from their lead rooms, server-worded, and the way to the one place
            they are changed: this listing's room mapping page (§3.20). */}
        {member.offsets && (
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-[22px] text-xs tabular-nums text-muted-foreground">
            <Percent className="h-3 w-3 shrink-0 text-primary/70" aria-hidden="true" />
            <span>{member.offsets.summary}</span>
            {member.offsets.href && (
              <Link
                href={member.offsets.href}
                aria-label={`${member.offsets.linkLabel} for ${member.name} on its room mapping`}
                className="rounded font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                {member.offsets.linkLabel}
              </Link>
            )}
          </p>
        )}
        {/* Why there is no Remove on this row — under the hotel it is about, not competing with the
            actions on the right. */}
        {canManageGroups && !member.canDecouple && member.decoupleBlockedReason && (
          <p className="mt-1 pl-[22px] text-[11px] leading-snug text-muted-foreground">
            {member.decoupleBlockedReason}
          </p>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {member.isLead && (
          <Link
            href={`${member.basePath}/pricing-rules/rooms-setup`}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            Manage pricing
            <ArrowRight className="h-3 w-3" />
          </Link>
        )}

        {member.mapping && member.mappingHref && (
          <Link
            href={member.mappingHref}
            className="inline-flex h-8 items-center rounded-lg border border-border/60 bg-background px-3 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
          >
            <Link2 className="mr-1.5 h-3.5 w-3.5" />
            Edit room mapping
          </Link>
        )}

        {canManageGroups && member.canDecouple && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg text-xs text-rose-500 hover:text-rose-600"
            onClick={() => onDecouple(member)}
          >
            <Unlink className="mr-1.5 h-3.5 w-3.5" />
            Remove
            {unpriced > 0 && <span className="ml-1.5 text-[10px] text-rose-400">({unpriced} unpriced)</span>}
          </Button>
        )}
      </div>
    </div>
  )
}

// ── No combined inventory yet ────────────────────────────────────────────────
function EmptyState({ canCreate, onCreate }: { canCreate: boolean; onCreate: () => void }) {
  return (
    <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
      <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
        <Layers className="mx-auto h-8 w-8 text-muted-foreground/40" />
        <p className="mt-3 text-sm font-medium text-foreground">No combined inventory, and most businesses never need one</p>
        <p className="mx-auto mt-1 max-w-xl text-xs leading-relaxed text-muted-foreground">
          Combined inventory is for one building listed several times in your PMS. One listing leads: its base rates,
          seasons, guardrails, targets and market analysis set the prices for every listing. Each room on a following
          listing is a Same room, sold at the price of the same room at the lead listing, or an Added room, set up like
          any other room.
        </p>
        <p className="mx-auto mt-2.5 max-w-xl text-xs leading-relaxed text-muted-foreground">
          Bookings on every listing count toward the building. Each listing still publishes its own prices to the PMS,
          and you can undo it at any time. If each of your buildings is listed once, this page is meant to be empty.
        </p>
        {canCreate && (
          <Button className="mt-5 rounded-lg" onClick={onCreate}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Create a combined inventory
          </Button>
        )}
      </div>
    </section>
  )
}
